/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { History, X, User, Clock, ArrowRight } from 'lucide-react';
import { EntreeJournal, EntreeJournalServeur } from './types.ts';
import { LIBELLE_PORTEE } from './BandeauDernierChangement.tsx';

interface JournalChangementsProps {
  estOuvert: boolean;
  journal: EntreeJournal[];
  journalServeur?: EntreeJournalServeur[];
  onFermer: () => void;
}

export const JournalChangements: React.FC<JournalChangementsProps> = ({
  estOuvert,
  journal,
  journalServeur = [],
  onFermer,
}) => {
  if (!estOuvert) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="titre-journal"
      className="fixed inset-y-0 right-0 z-50 w-full sm:w-96 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl p-4 flex flex-col select-none animate-in slide-in-from-right"
    >
      <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <h3 id="titre-journal" className="font-bold text-sm text-slate-900 dark:text-slate-100">
            Journal des changements en service
          </h3>
        </div>
        <button
          type="button"
          onClick={onFermer}
          aria-label="Fermer le journal"
          className="p-1 rounded-md text-slate-400 hover:text-slate-600 min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-3 space-y-2.5">
        {journal.length === 0 && journalServeur.length === 0 && (
          <div className="py-12 text-center text-xs text-slate-400">
            Aucun changement enregistré pour l'instant.
          </div>
        )}

        {journal.length > 0 && (
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Brouillon en cours (pas encore appliqué)</h4>
        )}
        {journal.map((entree) => (
          <div
            key={entree.id}
            className="p-3 rounded-xl border border-dashed border-amber-300 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/20 text-xs space-y-1.5"
          >
            <div className="flex items-center justify-between text-slate-500 text-[11px]">
              <div className="flex items-center gap-1 font-semibold text-slate-700 dark:text-slate-300">
                <User className="w-3 h-3 text-slate-400" />
                <span>{entree.auteur}</span>
              </div>
              <div className="flex items-center gap-1 font-mono">
                <Clock className="w-3 h-3" />
                <span>{entree.timestamp}</span>
              </div>
            </div>
            <p className="font-bold text-slate-900 dark:text-slate-100">{entree.description}</p>
          </div>
        ))}

        {journalServeur.length > 0 && (
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 pt-1">Changements appliqués</h4>
        )}
        {journalServeur.map((entree) => (
          <div
            key={entree.id}
            className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 text-xs space-y-1.5"
          >
            <div className="flex items-center justify-between text-slate-500 text-[11px]">
              <div className="flex items-center gap-1 font-semibold text-slate-700 dark:text-slate-300">
                <User className="w-3 h-3 text-slate-400" />
                <span>{entree.auteur}</span>
              </div>
              <div className="flex items-center gap-1 font-mono">
                <Clock className="w-3 h-3" />
                <span>{entree.horodatage}</span>
              </div>
            </div>
            <p className={`font-bold ${entree.annule ? 'line-through text-slate-400' : 'text-slate-900 dark:text-slate-100'}`}>{entree.description}</p>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              {LIBELLE_PORTEE[entree.portee]}{entree.annule ? ' · annulé' : ''}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
